import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import type { UseGameStateReturn } from '@/hooks/use-game-state';
import { Gamepad, Sparkles, Trophy, Dices, AlertCircle, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Trash2 } from 'lucide-react';
import { teams } from '@/config/teams';

interface Challenge {
  id: string;
  type: string;
  content: string;
  trigger: string;
  category: string;
}

export interface TeamViewProps {
  gameState: UseGameStateReturn;
  teamId: string;
  sessionId: string;
  userId: string;
  userName: string;
}

type TeamColorScheme = {
  primary: string;
  secondary: string;
  light: string;
  text: string;
  hover: string;
  border: string;
};

type TeamColors = {
  [key: number]: TeamColorScheme;
};

const teamColors: TeamColors = {
  0: {
    primary: 'bg-blue-600',
    secondary: 'bg-blue-400',
    light: 'bg-blue-100',
    text: 'text-blue-600',
    hover: 'hover:bg-blue-700',
    border: 'border-blue-200'
  },
  1: {
    primary: 'bg-emerald-600',
    secondary: 'bg-emerald-400',
    light: 'bg-emerald-100',
    text: 'text-emerald-600',
    hover: 'hover:bg-emerald-700',
    border: 'border-emerald-200'
  },
  2: {
    primary: 'bg-purple-600',
    secondary: 'bg-purple-400',
    light: 'bg-purple-100',
    text: 'text-purple-600',
    hover: 'hover:bg-purple-700',
    border: 'border-purple-200'
  }
};

function ErrorState() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
      <Card className="p-8 text-center max-w-md">
        <div className="mb-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
        </div>
        <h2 className="text-2xl font-bold text-red-600 mb-4">{t('game.error.title')}</h2>
        <p className="text-muted-foreground mb-6">
          {t('game.error.description')}
        </p>
        <Button
          onClick={() => {
            const basePath = import.meta.env.PROD ? '/storm' : '';
            window.location.href = `${window.location.origin}${basePath}${window.location.pathname}${window.location.search}`;
          }}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {t('game.error.retry')}
        </Button>
      </Card>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
      <Card className="p-8 text-center">
        <Dices className="h-12 w-12 mx-auto mb-4 text-purple-600 animate-spin" />
        <h2 className="text-xl font-bold mb-2">{message}</h2>
      </Card>
    </div>
  );
}

export function TeamView({ gameState, teamId, userId, userName }: TeamViewProps) {
  const { t } = useTranslation();
  
  // Get feminine mode from game state (controlled by MasterView)
  const isFeminineMode = gameState.state?.gameBoard?.isFeminineMode || false;

  // Get team name based on feminine mode
  const getTeamName = (team: any) => {
    const nameKey = isFeminineMode ? team.nameFeminine : team.name;
    return t(nameKey);
  };
  const { state, status, actions } = gameState;
  const [currentIdea, setCurrentIdea] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const maxCharacters = 500;

  // Update rolling state when challenge changes
  useEffect(() => {
    if (state?.gameBoard?.teamChallenges?.[teamId]) {
      setIsRolling(false);
    }
  }, [state?.gameBoard?.teamChallenges?.[teamId], teamId]);

  // If no state or actions are available, show loading
  if (!state || !actions) {
    return <LoadingState message={t('game.messages.connecting')} />;
  }

  // Show error state
  if (status === "error") {
    return <ErrorState />;
  }

  // Show connecting state
  if (status === "connecting") {
    return <LoadingState message={t('game.messages.initializing')} />;
  }

  const team = state.teams[teamId];
  const challenge = state.gameBoard?.teamChallenges?.[teamId];
  const sharedIdeas = team?.draftIdeas || [];
  
  // Get pending submission if any
  const pendingSubmission = state.gameBoard?.submissionQueue?.find(
    sub => sub.teamId === teamId
  );
  
  // Calculate team index and color scheme
  const teamIndex = Math.max(0, Math.min(2, parseInt(teamId.replace('team', '')) - 1));
  const teamColor = teamColors[teamIndex] || teamColors[0];
  const teamConfig = teams.find(t => t.id === teamId);

  // Handle sharing the current idea
  const handleShare = () => {
    if (currentIdea.trim()) {
      actions.updateDraftIdea(currentIdea.trim());
      setCurrentIdea('');
      setCharacterCount(0);
    }
  };

  // Handle submitting ideas
  const handleSubmitIdeas = () => {
    if (sharedIdeas.length > 0) {
      actions.submitIdeas(sharedIdeas.map(idea => idea.content));
    }
  };

  // Handle Ctrl/Cmd + Enter shortcut
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleShare();
    }
  };

  // Show waiting state if team is not yet initialized
  if (!team) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <Dices className="h-12 w-12 mx-auto mb-4 text-purple-600 animate-spin" />
          <h2 className="text-xl font-bold mb-4">{t('game.team.waiting')}</h2>
          <p className="text-muted-foreground mb-4">
            {t('game.team.joiningAs', { name: userName })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('game.team.id')}: {teamConfig ? getTeamName(teamConfig) : teamId}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto flex gap-6">
        {/* Left Section - Challenge and Progress */}
        <div className="w-1/2 space-y-8">
          {/* Challenge Display */}
          <Card className={cn(
            "p-6 border-2",
            teamColor?.border || 'border-gray-200'
          )}>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                  {t('game.challenge.title')}
                </h2>
                <Badge variant="outline" className={cn(teamColor.text, "text-sm")}>
                  {teamConfig ? getTeamName(teamConfig) : teamId}
                </Badge>
              </div>

              {challenge ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className={cn(
                    "p-4 rounded-lg",
                    teamColor.light
                  )}>
                    <h3 className="text-sm font-medium mb-2">{t('game.challenge.trigger')}</h3>
                    <p className={cn("text-xl font-bold", teamColor.text)}>
                      {isRolling ? "🎲" : challenge.trigger || "?"}
                    </p>
                  </div>
                  <div className={cn(
                    "p-4 rounded-lg",
                    teamColor.light
                  )}>
                    <h3 className="text-sm font-medium mb-2">{t('game.challenge.category')}</h3>
                    <p className={cn("text-xl font-bold", teamColor.text)}>
                      {isRolling ? "🎲" : challenge.category || "?"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Dices className="h-12 w-12 mb-4 animate-bounce" />
                  <p>{t('game.challenge.waitingForChallenge')}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Team Progress Section */}
          <div className="space-y-6">
            {Object.entries(state.teams).map(([currentTeamId, currentTeam]) => {
              const currentTeamIndex = Math.max(0, Math.min(2, parseInt(currentTeamId.replace('team', '')) - 1));
              const currentTeamColor = teamColors[currentTeamIndex] || teamColors[0];
              const currentTeamConfig = teams.find(t => t.id === currentTeamId);
              
              return (
                <Card key={currentTeamId} className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 w-24">
                      {currentTeamIndex === 0 && <Gamepad className={`h-6 w-6 ${currentTeamColor.text}`} />}
                      {currentTeamIndex === 1 && <Sparkles className={`h-6 w-6 ${currentTeamColor.text}`} />}
                      {currentTeamIndex === 2 && <Trophy className={`h-6 w-6 ${currentTeamColor.text}`} />}
                      <span className="font-medium">
                        {currentTeamConfig ? getTeamName(currentTeamConfig) : currentTeamId}
                      </span>
                    </div>
                    
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 flex gap-1 relative">
                        {[...Array(6)].map((_, i) => {
                          const teamLevel = currentTeam.level || 0;
                          const isCompleted = i < teamLevel;
                          const isCurrent = i === teamLevel;
                          
                          const isPending = state.gameBoard?.submissionQueue?.some(
                            (sub: any) => sub.teamId === currentTeamId && !sub.validated
                          );

                          return (
                            <div key={i} className="relative flex-1">
                              <div className={cn(
                                "h-16 w-full rounded-lg transition-all duration-300 relative",
                                isCompleted ? currentTeamColor.primary : // Completed steps
                                isCurrent ? currentTeamColor.secondary : // Current step
                                "bg-gray-200", // Future steps
                                isPending && isCurrent && "animate-pulse"
                              )}>
                                {/* Step Content */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  {i === 5 ? (
                                    <Trophy className={cn(
                                      "w-6 h-6",
                                      isCompleted ? "text-white" : 
                                      isCurrent ? "text-white" : 
                                      "text-gray-400"
                                    )} />
                                  ) : i === 0 ? (
                                    <Dices className={cn(
                                      "w-6 h-6",
                                      isCompleted ? "text-white opacity-50" :
                                      isCurrent ? "text-white animate-bounce" :
                                      "text-gray-400"
                                    )} />
                                  ) : (
                                    <span className={cn(
                                      "font-bold",
                                      isCompleted ? "text-white opacity-50" :
                                      isCurrent ? "text-white" :
                                      "text-gray-400"
                                    )}>
                                      {i}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Right Section - Team Submissions */}
        <div className="w-1/2">
          <Card className="p-6 sticky top-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{t('game.team.submissions')}</h3>
                {sharedIdeas.length > 0 && !pendingSubmission && (
                  <Badge variant="secondary">
                    {sharedIdeas.length} {t('game.team.ideas')}
                  </Badge>
                )}
              </div>
              
              {/* Ideas Input - Only show if no pending submission */}
              {!pendingSubmission && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Textarea
                      id="idea-input"
                      value={currentIdea}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= maxCharacters) {
                          setCurrentIdea(value);
                          setCharacterCount(value.length);
                        }
                      }}
                      onKeyDown={handleKeyPress}
                      placeholder={t('game.challenge.placeholder')}
                      className={cn(
                        "min-h-[40px] max-h-[40px] bg-white/50 backdrop-blur-sm resize-none",
                        characterCount > maxCharacters && "border-red-500"
                      )}
                    />
                    <Button
                      onClick={handleShare}
                      disabled={!currentIdea.trim() || currentIdea.length > maxCharacters}
                      className={cn(
                        teamColor?.primary || 'bg-gray-600',
                        teamColor?.hover || 'hover:bg-gray-700',
                        "text-white h-10 px-3"
                      )}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{t('game.team.shortcutHint')}</span>
                    <span className={cn(
                      characterCount > maxCharacters && "text-red-500",
                      characterCount > maxCharacters * 0.8 && "text-yellow-500"
                    )}>
                      {characterCount}/{maxCharacters}
                    </span>
                  </div>
                </div>
              )}

              {/* Ideas List */}
              <ScrollArea className="h-[400px]">
                <div className="space-y-2 pr-4">
                  {/* Pending Submission */}
                  {pendingSubmission && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-muted-foreground">
                        {t('game.team.pendingSubmission')}
                      </h4>
                      {pendingSubmission.content.map((idea, index) => (
                        <Card key={`pending-${index}`} className="p-3 bg-yellow-50">
                          <p className="text-sm">{idea}</p>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Draft Ideas - Only show if no pending submission */}
                  {!pendingSubmission && (
                    <>
                      {sharedIdeas.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>{t('game.team.noIdeas')}</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {sharedIdeas.map((idea) => (
                            <Card key={idea.id} className={cn(
                              "p-3 border group flex items-center gap-2",
                              teamColor?.border || 'border-gray-200'
                            )}>
                              <p className="text-sm flex-1">{idea.content}</p>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => actions.deleteDraftIdea(idea.id)}
                                className="opacity-0 group-hover:opacity-100 h-6 w-6"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </Card>
                          ))}
                          <Button
                            onClick={handleSubmitIdeas}
                            className="w-full bg-green-600 hover:bg-green-700 text-white mt-4"
                          >
                            {t('game.actions.submitToMaster')}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
